// Game Settings + the points ledger — the per-user rules the Master Book
// (Update Directive 6 Oct 2026, rulings 1.5–1.8, 1.11, 2.2–2.3, 5.2) moved
// out of hard-coded engine constants. Pure: types, shipped defaults, a
// normaliser, the award table, and SyQi's conflict check. Persisted inside
// `user_settings.oracle_sector_config` as `game` (settings) and `ledger`
// (per-day award marks) through `useCompassConfig` (sectorConfig.ts), so no
// schema change is needed.

/** "points" is a working name — a new unit name is coming (Book [OPEN]).
 *  Every user-facing use of the unit should render this constant. */
export const POINTS_UNIT = 'pts';
export const POINTS_UNIT_LONG = 'points';

/** Approved award table (Book Part V · V.8, §5.2 sign-off 7 Oct 2026). */
export const AWARD = {
  /** Each completed Step action (every task done in the day). */
  STEP_ACTION: 1,
  /** Each Today-orb ORDER row green (all five = an ordered day = +5). */
  ORDER_ROW_GREEN: 1,
  /** NUMA: Charge, Load and Flow all in their green bands at the day's close. */
  NUMA_THREE_GREENS: 2,
  /** The evening session completed (it closes the day). */
  EVENING_SESSION: 1,
  /** Unchanged rungs. */
  AIM: 30,
  GOAL: 150,
} as const;
export const ACQUIRE_POINTS = 400;

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday

export interface GreenBands {
  /** Charge green at or above this (0–10). */
  chargeMin: number;
  /** Load green at or below personal baseline × this factor. */
  loadMaxOfBaseline: number;
  /** Flow/RATE green at or above this (%). */
  flowMin: number;
}

export interface GameSettings {
  /** "HH:MM" local. Charge's background drain starts here (2.2), and the
   *  morning session opens O·R·D on the Today orb. */
  morningSessionTime: string;
  /** "HH:MM" local. The evening session opens R and closes the day. */
  eveningSessionTime: string;
  /** Days that make up the week's counted slots (week shape). */
  weekDays: Weekday[];
  /** Whether Saturday/Sunday count toward windows even when outside weekDays. */
  countWeekends: boolean;
  /** The weekly boundary: the evening session on this day closes the week and
   *  opens the next (1.11). */
  weekBoundaryDay: Weekday;
  /** Ordered days needed in a week for the Aim (default 4 of 5). */
  aimThreshold: number;
  /** Goal window length in weeks, and ordered weeks needed in it. */
  goalWindowWeeks: number;
  goalThreshold: number;
  /** Endeavour window in months, and ordered months needed. */
  endeavourWindowMonths: number;
  endeavourThreshold: number;
  /** Season length in months. */
  seasonMonths: number;
  greenBands: GreenBands;
  /** Widget stack for each session; CLIO is in the evening by default (1.6). */
  morningStack: string[];
  eveningStack: string[];
  /** Deity acquisition ladder (Update Directive 18 Oct 2026 §2). Default 3 of 4 at every rung. */
  ladder: LadderRules;
  /** Seat score window + the minimum days of marks before a score shows (§5). */
  seatWindowDays: number;
  seatMinDays: number;
  /** Alignment Score windows per STAGE orb, in days (§7). Today = 1. */
  alignmentWindows: { aim: number; goal: number; endeavour: number };
}

/** Rung sizes + thresholds of the one deity ladder (§2). A Day qualifies with
 *  `dayThreshold` of `dayActions` required actions AND the Day orb done; an Aim
 *  with `aimThreshold` of `aimDays` scheduled Days; a Goal (fixed 4-week block)
 *  with `goalThreshold` of `goalAims` Aims; Acquired at `goalsToAcquire` Goals. */
export interface LadderRules {
  dayActions: number;
  dayThreshold: number;
  aimDays: number;
  aimThreshold: number;
  goalAims: number;
  goalThreshold: number;
  goalsToAcquire: number;
}

export const DEFAULT_LADDER: LadderRules = {
  dayActions: 4,
  dayThreshold: 3,
  aimDays: 4,
  aimThreshold: 3,
  goalAims: 4,
  goalThreshold: 3,
  goalsToAcquire: 3,
};

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  morningSessionTime: '07:00',
  eveningSessionTime: '20:00',
  weekDays: [1, 2, 3, 4, 5],
  countWeekends: false,
  weekBoundaryDay: 0,
  aimThreshold: 4,
  goalWindowWeeks: 5,
  goalThreshold: 4,
  endeavourWindowMonths: 5,
  endeavourThreshold: 4,
  seasonMonths: 3,
  greenBands: { chargeMin: 6, loadMaxOfBaseline: 1, flowMin: 80 },
  // ORACLE retired (18 Oct 2026): its readout lives in ATLAS; NUMA runs in the morning session.
  morningStack: ['numa', 'telos', 'kyrs'],
  eveningStack: ['clio'],
  ladder: DEFAULT_LADDER,
  seatWindowDays: 30,
  seatMinDays: 8,
  alignmentWindows: { aim: 7, goal: 28, endeavour: 84 },
};

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;
const num = (v: unknown, d: number, lo: number, hi: number) =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d;
const time = (v: unknown, d: string) => (typeof v === 'string' && HHMM.test(v) ? v : d);
const days = (v: unknown, d: Weekday[]) =>
  Array.isArray(v) && v.every((x) => Number.isInteger(x) && x >= 0 && x <= 6) && v.length
    ? ([...new Set(v as Weekday[])].sort() as Weekday[])
    : d;
const stack = (v: unknown, d: string[]) => (Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : d);

export function normalizeGameSettings(raw: unknown): GameSettings {
  const D = DEFAULT_GAME_SETTINGS;
  if (!raw || typeof raw !== 'object') return D;
  const r = raw as Record<string, unknown>;
  const gb = (r.greenBands ?? {}) as Record<string, unknown>;
  return {
    morningSessionTime: time(r.morningSessionTime, D.morningSessionTime),
    eveningSessionTime: time(r.eveningSessionTime, D.eveningSessionTime),
    weekDays: days(r.weekDays, D.weekDays),
    countWeekends: typeof r.countWeekends === 'boolean' ? r.countWeekends : D.countWeekends,
    weekBoundaryDay: num(r.weekBoundaryDay, D.weekBoundaryDay, 0, 6) as Weekday,
    aimThreshold: num(r.aimThreshold, D.aimThreshold, 1, 7),
    goalWindowWeeks: num(r.goalWindowWeeks, D.goalWindowWeeks, 1, 12),
    goalThreshold: num(r.goalThreshold, D.goalThreshold, 1, 12),
    endeavourWindowMonths: num(r.endeavourWindowMonths, D.endeavourWindowMonths, 1, 24),
    endeavourThreshold: num(r.endeavourThreshold, D.endeavourThreshold, 1, 24),
    seasonMonths: num(r.seasonMonths, D.seasonMonths, 1, 12),
    greenBands: {
      chargeMin: num(gb.chargeMin, D.greenBands.chargeMin, 0, 10),
      loadMaxOfBaseline: num(gb.loadMaxOfBaseline, D.greenBands.loadMaxOfBaseline, 0.1, 3),
      flowMin: num(gb.flowMin, D.greenBands.flowMin, 0, 200),
    },
    // A saved stack that still opens the retired ORACLE gets ATLAS instead.
    morningStack: stack(r.morningStack, D.morningStack).map((x) => (x === 'oracle' ? 'telos' : x)),
    eveningStack: stack(r.eveningStack, D.eveningStack).map((x) => (x === 'oracle' ? 'telos' : x)),
    ladder: normalizeLadder(r.ladder),
    seatWindowDays: num(r.seatWindowDays, D.seatWindowDays, 7, 120),
    seatMinDays: num(r.seatMinDays, D.seatMinDays, 1, 60),
    alignmentWindows: {
      aim: num((r.alignmentWindows as any)?.aim, D.alignmentWindows.aim, 2, 31),
      goal: num((r.alignmentWindows as any)?.goal, D.alignmentWindows.goal, 7, 120),
      endeavour: num((r.alignmentWindows as any)?.endeavour, D.alignmentWindows.endeavour, 28, 366),
    },
  };
}

export function normalizeLadder(raw: unknown): LadderRules {
  const D = DEFAULT_LADDER;
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const dayActions = num(r.dayActions, D.dayActions, 1, 12);
  const aimDays = num(r.aimDays, D.aimDays, 1, 7);
  const goalAims = num(r.goalAims, D.goalAims, 1, 8);
  return {
    dayActions,
    dayThreshold: num(r.dayThreshold, D.dayThreshold, 1, dayActions),
    aimDays,
    aimThreshold: num(r.aimThreshold, D.aimThreshold, 1, aimDays),
    goalAims,
    goalThreshold: num(r.goalThreshold, D.goalThreshold, 1, goalAims),
    goalsToAcquire: num(r.goalsToAcquire, D.goalsToAcquire, 1, 12),
  };
}

/** SyQi's conflict check (1.7): human-readable warnings, empty when coherent. */
export function settingsConflicts(s: GameSettings): string[] {
  const out: string[] = [];
  const slots = s.countWeekends ? 7 : s.weekDays.length;
  if (s.aimThreshold > slots) out.push(`Your Aim needs ${s.aimThreshold} ordered days but your week has only ${slots} counted days.`);
  if (s.goalThreshold > s.goalWindowWeeks) out.push(`Your Goal needs ${s.goalThreshold} ordered weeks inside a ${s.goalWindowWeeks}-week window.`);
  if (s.endeavourThreshold > s.endeavourWindowMonths)
    out.push(`Your Endeavour needs ${s.endeavourThreshold} ordered months inside a ${s.endeavourWindowMonths}-month window.`);
  if (s.endeavourWindowMonths > s.seasonMonths)
    out.push(`Your Endeavour window (${s.endeavourWindowMonths} months) is longer than a season (${s.seasonMonths} months), so a season can end before an Endeavour can be ordered.`);
  if (minutesOf(s.eveningSessionTime) <= minutesOf(s.morningSessionTime))
    out.push('Your evening session is set before your morning session.');
  if (!s.eveningStack.includes('clio')) out.push('CLIO is not in your evening session, so the day has no Revise close.');
  return out;
}

export function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/** The moment today's Charge drain starts (2.2): today's morning-session time. */
export function wakeOf(now: Date, s: GameSettings): Date {
  const d = new Date(now);
  const mins = minutesOf(s.morningSessionTime);
  d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
  return d;
}

/** Start (local midnight) of the week containing `d`, where the week opens the
 *  day after `weekBoundaryDay` (default boundary Sunday evening → week opens
 *  Monday 00:00 in calendar terms; the boundary *session* runs Sunday evening). */
export function weekStartOf(d: Date, s: GameSettings = DEFAULT_GAME_SETTINGS): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const open = (s.weekBoundaryDay + 1) % 7;
  const back = (x.getDay() - open + 7) % 7;
  x.setDate(x.getDate() - back);
  return x;
}

/** Is `now` inside the boundary session window (boundary day, at/after the
 *  evening session time)? */
export function isWeekBoundaryEvening(now: Date, s: GameSettings): boolean {
  return now.getDay() === s.weekBoundaryDay && now.getHours() * 60 + now.getMinutes() >= minutesOf(s.eveningSessionTime);
}

/** One day's award marks. Counts, not points — points derive via AWARD. */
export interface DayLedger {
  stepActions: number;
  rowsGreen: number; // 0–5
  numaThreeGreens: boolean;
  eveningSession: boolean;
  /** The morning session ran (it opens the Day orb; NUMA runs in it — §6). */
  morningSession?: boolean;
  /** Manual completion marks the user made for goals the tools can't track. */
  manualMarks?: string[];
}
export type PointsLedger = Record<string, DayLedger>; // key = YYYY-MM-DD

export const EMPTY_DAY: DayLedger = { stepActions: 0, rowsGreen: 0, numaThreeGreens: false, eveningSession: false };

export function dayPoints(d: DayLedger): number {
  return (
    d.stepActions * AWARD.STEP_ACTION +
    Math.min(5, d.rowsGreen) * AWARD.ORDER_ROW_GREEN +
    (d.numaThreeGreens ? AWARD.NUMA_THREE_GREENS : 0) +
    (d.eveningSession ? AWARD.EVENING_SESSION : 0)
  );
}

/** Ordered day (1.5) = all five ORDER rows green on the Today orb. */
export const isOrderedDay = (d: DayLedger | undefined) => !!d && d.rowsGreen >= 5;

export function normalizeLedger(raw: unknown): PointsLedger {
  if (!raw || typeof raw !== 'object') return {};
  const out: PointsLedger = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(k) || !v || typeof v !== 'object') continue;
    const r = v as Record<string, unknown>;
    out[k] = {
      stepActions: num(r.stepActions, 0, 0, 999),
      rowsGreen: num(r.rowsGreen, 0, 0, 5),
      numaThreeGreens: r.numaThreeGreens === true,
      eveningSession: r.eveningSession === true,
      morningSession: r.morningSession === true ? true : undefined,
      manualMarks: Array.isArray(r.manualMarks) ? (r.manualMarks as unknown[]).filter((x): x is string => typeof x === 'string') : undefined,
    };
  }
  return out;
}

/** Day orb done (§6): the morning session opened the day and the evening
 *  session closed it. Without it the day earns no Day rung for any deity and
 *  counts as missed execution in every sector's alignment. */
export const isDayOrbDone = (d: DayLedger | undefined) => !!d && d.eveningSession && d.morningSession === true;
